import { useEffect, useRef, useState, useCallback } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface MeterReading {
  meter_id: number;
  serial: string;
  cumulative: string;
  flow_rate: string;
  pressure: string;
  valve_open: boolean;
  timestamp: string;
}

export interface HistoryPoint {
  time: string;       // HH:MM:SS label for chart
  cumulative: number;
  flow_rate: number;
  pressure: number;
  timestamp: string;  // ISO string
}

export interface DashboardUpdate {
  type: string;
  total_meters?: number;
  active_meters?: number;
  total_balance?: number;
  monthly_revenue?: number;
  alerts?: SystemAlert[];
}

export interface SystemAlert {
  id: string;
  level: "info" | "warning" | "critical";
  message: string;
  serial?: string;
  timestamp: string;
}

// ── Single meter live stream ──────────────────────────────────────────────────
export function useLiveMetrics(meterId: number | null) {
  const [reading, setReading]     = useState<MeterReading | null>(null);
  const [history, setHistory]     = useState<HistoryPoint[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const MAX_HISTORY = 60; // keep last 60 data points

  useEffect(() => {
    if (!meterId) return;

    const wsBase = process.env.REACT_APP_WS_URL || "wss://meters.mutabletech.co.ke";
    const ws = new WebSocket(`${wsBase}/ws/meter/${meterId}/`);
    wsRef.current = ws;

    ws.onopen  = () => setConnected(true);
    ws.onclose = () => setConnected(false);

    ws.onmessage = (e) => {
      try {
        const data: MeterReading = JSON.parse(e.data);
        setReading(data);

        // Append to rolling history
        const ts  = new Date(data.timestamp);
        const label = ts.toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

        setHistory((prev) => {
          const next = [
            ...prev,
            {
              time:       label,
              cumulative: parseFloat(data.cumulative),
              flow_rate:  parseFloat(data.flow_rate),
              pressure:   parseFloat(data.pressure),
              timestamp:  data.timestamp,
            },
          ];
          return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
        });
      } catch {}
    };

    return () => { ws.close(); wsRef.current = null; };
  }, [meterId]);

  const clearHistory = useCallback(() => setHistory([]), []);

  return { reading, history, connected, clearHistory };
}

// ── Dashboard-wide live stream ────────────────────────────────────────────────
export function useDashboardLive() {
  const [update, setUpdate]       = useState<DashboardUpdate | null>(null);
  const [alerts, setAlerts]       = useState<SystemAlert[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const wsBase = process.env.REACT_APP_WS_URL || "wss://meters.mutabletech.co.ke";
    const ws = new WebSocket(`${wsBase}/ws/dashboard/`);
    wsRef.current = ws;

    ws.onopen  = () => setConnected(true);
    ws.onclose = () => setConnected(false);

    ws.onmessage = (e) => {
      try {
        const data: DashboardUpdate = JSON.parse(e.data);
        setUpdate(data);

        if (data.alerts?.length) {
          setAlerts((prev) => {
            const merged = [...data.alerts!, ...prev];
            return merged.slice(0, 20); // keep last 20 alerts
          });
        }
      } catch {}
    };

    return () => { ws.close(); wsRef.current = null; };
  }, []);

  return { update, alerts, connected };
}

// ── System-wide alerts stream ─────────────────────────────────────────────────
export function useLiveAlerts() {
  const [alerts, setAlerts]       = useState<SystemAlert[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const wsBase = process.env.REACT_APP_WS_URL || "wss://meters.mutabletech.co.ke";
    const ws = new WebSocket(`${wsBase}/ws/alerts/`);
    wsRef.current = ws;

    ws.onopen  = () => setConnected(true);
    ws.onclose = () => setConnected(false);

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        const alert: SystemAlert = {
          id:        data.id || Date.now().toString(),
          level:     data.level || "info",
          message:   data.message || data.reason || "",
          serial:    data.serial,
          timestamp: data.timestamp || new Date().toISOString(),
        };
        setAlerts((prev) => [alert, ...prev].slice(0, 50));
      } catch {}
    };

    return () => { ws.close(); wsRef.current = null; };
  }, []);

  return { alerts, connected };
}
